import {
    ActionCreatorWithPayload,
    configureStore,
    combineReducers
} from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import { clientsApi } from '@apis/clients/Clients.api';
import { countriesApi } from '@apis/countries/Countries.api';
import { customerDeliveryOrderByProductApi } from '@apis/customerDeliveryOrderByProduct/CustomerDeliveryOrderByProductApi';
import { dundasApi } from '@apis/dundas/Dundas.api';
import { filesApi } from '@apis/files/Files.api';
import { googleApi } from '@apis/google/Google.api';
import { hazardApi } from '@apis/hazard/Hazard.api';
import {
    inboundByBulkApi,
    inboundByBulkDefectiveApi
} from '@apis/inboundByBulk/InboundByBulk.api';
import {
    inboundReplenishmentApi,
    inboundOrderByProductDefectiveApi
} from '@apis/inboundReplenishment/InboundReplenishment.api';
import { inboundReturnApi } from '@apis/inboundReturn/InboundReturn.api';
import { closestWarehousesApi } from '@apis/manageInventory/GetClosestWarehouses.api';
import { locateProductApi } from '@apis/manageInventory/LocateProduct.api';
import { materialsInReviewApi } from '@apis/materialsInReview/MaterialsInReview.api';
import {
    materialsTransferByWarehouseApi,
    materialsTransferByWarehouseDefectiveApi
} from '@apis/materialsTransferByWarehouse/MaterialsTransferByWarehouse.api';
import { notificationsApi } from '@apis/notifications/Notifications.api';
import {
    outboundByBulkApi,
    outboundByBulkDefectiveApi
} from '@apis/outboundByBulk/OutboundByBulk.api';
import {
    outboundOrderByWarehouseApi,
    outboundOrderByWarehouseDefectiveApi
} from '@apis/outboundOrderByWarehouse/OutboundOrderByWarehouse.api';
import { permissionsApi } from '@apis/permissions/Permissions.api';
import { profilesApi } from '@apis/profiles/Profiles.api';
import { trackOrdersApi } from '@apis/trackOrders/TrackOrders.api';
import {
    transfersByBulkApi,
    transfersByBulkDefectiveApi
} from '@apis/transfersByBulk/TransfersByBulk.api';
import { usersApi } from '@apis/users/Users.api';
import { warehouseApi } from '@apis/warehouse/Warehouse.api';
import { ErrorRedirectBlocker } from '@store/middlewares/errorRedirectBlocker/ErrorRedirectBlocker';
import { ErrorLogger } from '@store/middlewares/logger/Logger';
import { activeOrderSlice } from '@store/slices/activeOrder/activeOrder';
import { adminPanelSlice } from '@store/slices/adminPanel/AdminPanel';
import { bulkSlice } from '@store/slices/bulk/bulk';
import { dundasSlice } from '@store/slices/dundas/Dundas';
import { filesSlice } from '@store/slices/files/files';
import { notificationsSlice } from '@store/slices/notifications/notifications';
import { preCheckMasterSlice } from '@store/slices/preCheckMaster/preCheckMaster';
import { productMasterSlice } from '@store/slices/productMaster/productMaster';
import { serialsMasterSlice } from '@store/slices/serialsMaster/serialsMaster';
import { trackOrdersSlice } from '@store/slices/trackOrders/trackOrders';
import { userSlice } from '@store/slices/user/user';

const middlewares = [
    ErrorLogger,
    ErrorRedirectBlocker,
    hazardApi.middleware,
    customerDeliveryOrderByProductApi.middleware,
    outboundOrderByWarehouseApi.middleware,
    outboundOrderByWarehouseDefectiveApi.middleware,
    inboundReplenishmentApi.middleware,
    inboundOrderByProductDefectiveApi.middleware,
    inboundReturnApi.middleware,
    materialsTransferByWarehouseApi.middleware,
    materialsTransferByWarehouseDefectiveApi.middleware,
    inboundByBulkApi.middleware,
    inboundByBulkDefectiveApi.middleware,
    outboundByBulkApi.middleware,
    outboundByBulkDefectiveApi.middleware,
    transfersByBulkApi.middleware,
    transfersByBulkDefectiveApi.middleware,
    filesApi.middleware,
    dundasApi.middleware,
    countriesApi.middleware,
    clientsApi.middleware,
    usersApi.middleware,
    warehouseApi.middleware,
    closestWarehousesApi.middleware,
    locateProductApi.middleware,
    trackOrdersApi.middleware,
    materialsInReviewApi.middleware,
    permissionsApi.middleware,
    profilesApi.middleware,
    notificationsApi.middleware,
    googleApi.middleware
];

const rootReducer = combineReducers({
    [userSlice.name]: userSlice.reducer,
    [activeOrderSlice.name]: activeOrderSlice.reducer,
    [bulkSlice.name]: bulkSlice.reducer,
    [filesSlice.name]: filesSlice.reducer,
    [productMasterSlice.name]: productMasterSlice.reducer,
    [preCheckMasterSlice.name]: preCheckMasterSlice.reducer,
    [serialsMasterSlice.name]: serialsMasterSlice.reducer,
    [trackOrdersSlice.name]: trackOrdersSlice.reducer,
    [notificationsSlice.name]: notificationsSlice.reducer,
    [adminPanelSlice.name]: adminPanelSlice.reducer,
    [dundasSlice.name]: dundasSlice.reducer,
    [hazardApi.reducerPath]: hazardApi.reducer,
    [customerDeliveryOrderByProductApi.reducerPath]:
        customerDeliveryOrderByProductApi.reducer,
    [outboundOrderByWarehouseApi.reducerPath]:
        outboundOrderByWarehouseApi.reducer,
    [outboundOrderByWarehouseDefectiveApi.reducerPath]:
        outboundOrderByWarehouseDefectiveApi.reducer,
    [inboundReplenishmentApi.reducerPath]: inboundReplenishmentApi.reducer,
    [inboundReturnApi.reducerPath]: inboundReturnApi.reducer,
    [inboundOrderByProductDefectiveApi.reducerPath]:
        inboundOrderByProductDefectiveApi.reducer,
    [inboundByBulkApi.reducerPath]: inboundByBulkApi.reducer,
    [inboundByBulkDefectiveApi.reducerPath]: inboundByBulkDefectiveApi.reducer,
    [filesApi.reducerPath]: filesApi.reducer,
    [materialsTransferByWarehouseApi.reducerPath]:
        materialsTransferByWarehouseApi.reducer,
    [materialsTransferByWarehouseDefectiveApi.reducerPath]:
        materialsTransferByWarehouseDefectiveApi.reducer,
    [warehouseApi.reducerPath]: warehouseApi.reducer,
    [closestWarehousesApi.reducerPath]: closestWarehousesApi.reducer,
    [locateProductApi.reducerPath]: locateProductApi.reducer,
    [trackOrdersApi.reducerPath]: trackOrdersApi.reducer,
    [materialsInReviewApi.reducerPath]: materialsInReviewApi.reducer,
    [countriesApi.reducerPath]: countriesApi.reducer,
    [clientsApi.reducerPath]: clientsApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [permissionsApi.reducerPath]: permissionsApi.reducer,
    [profilesApi.reducerPath]: profilesApi.reducer,
    [dundasApi.reducerPath]: dundasApi.reducer,
    [outboundByBulkApi.reducerPath]: outboundByBulkApi.reducer,
    [outboundByBulkDefectiveApi.reducerPath]:
        outboundByBulkDefectiveApi.reducer,
    [transfersByBulkApi.reducerPath]: transfersByBulkApi.reducer,
    [googleApi.reducerPath]: googleApi.reducer,
    [transfersByBulkDefectiveApi.reducerPath]:
        transfersByBulkDefectiveApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer
});

const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware().concat(...middlewares)
});

export interface Action<A> {
    type: string;
    payload: A;
}

export type ActionCreator<T> = ActionCreatorWithPayload<T>;

export const useAppDispatch = () => useDispatch<typeof store.dispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export type RootState = ReturnType<typeof store.getState>;
export default store;
